import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import "./HubHome.css";

// Generate particles for background
const generateParticles = (count) => {
    return Array.from({ length: count }, (_, i) => ({
        id: i,
        x: Math.random() * 100,
        y: Math.random() * 100,
        size: Math.random() * 3 + 1,
        duration: Math.random() * 20 + 10,
        delay: Math.random() * 5,
        opacity: Math.random() * 0.5 + 0.2,
    }));
};

// Hub nodes configuration
const hubNodes = [
    {
        id: "order",
        label: "Order Panel",
        sublabel: "Sipariş Yönetimi",
        url: "https://order.modelerman.com",
        external: true,
        angle: -90, // Top
        color: "#7c5cff",
        icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2" />
                <rect x="9" y="3" width="6" height="4" rx="1" />
                <path d="M9 12h6M9 16h6" />
            </svg>
        ),
    },
    {
        id: "portfolio",
        label: "Portfolio",
        sublabel: "3D Projeler",
        url: "https://modelerman.com/coming-soon",
        external: true,
        angle: -18, // Top-right
        color: "#B49B57",
        icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <path d="M21 15l-5-5L5 21" />
            </svg>
        ),
    },
    {
        id: "services",
        label: "Hizmetler",
        sublabel: "Render & CGI",
        url: "https://modelerman.com/coming-soon",
        external: true,
        angle: 54, // Bottom-right
        color: "#22c55e",
        icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M12 2L2 7l10 5 10-5-10-5z" />
                <path d="M2 17l10 5 10-5" />
                <path d="M2 12l10 5 10-5" />
            </svg>
        ),
    },
    {
        id: "store",
        label: "Mağaza",
        sublabel: "Dijital & Fiziksel",
        url: "https://modelerman.com/coming-soon",
        external: true,
        angle: 126, // Bottom-left
        color: "#f59e0b",
        icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z" />
                <line x1="3" y1="6" x2="21" y2="6" />
                <path d="M16 10a4 4 0 01-8 0" />
            </svg>
        ),
    },
    {
        id: "education",
        label: "Eğitim",
        sublabel: "Kurslar & Workshop",
        url: "https://modelerman.com/coming-soon",
        external: true,
        angle: 198, // Left
        color: "#ec4899",
        icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M22 10v6M2 10l10-5 10 5-10 5z" />
                <path d="M6 12v5c0 2 2 3 6 3s6-1 6-3v-5" />
            </svg>
        ),
    },
];

export default function HubHome() {
    const [activeNode, setActiveNode] = useState(null);
    const [isLoaded, setIsLoaded] = useState(false);
    const [showQR, setShowQR] = useState(false);

    // Generate particles once
    const particles = useMemo(() => generateParticles(50), []);

    useEffect(() => {
        const timer = setTimeout(() => setIsLoaded(true), 100);
        return () => clearTimeout(timer);
    }, []);

    const handleNodeHover = (nodeId) => {
        setActiveNode(nodeId);
    };

    const handleNodeLeave = () => {
        setActiveNode(null);
    };

    // Calculate node positions
    const centerX = 50;
    const centerY = 50;
    const radius = 35;

    return (
        <div className={`hub-home ${isLoaded ? "hub-home--loaded" : ""}`}>
            {/* Background Effects */}
            <div className="hub-bg">
                <div className="hub-bg__glow hub-bg__glow--1"></div>
                <div className="hub-bg__glow hub-bg__glow--2"></div>
                <div className="hub-bg__grid"></div>

                {/* Particle System */}
                <div className="hub-particles">
                    {particles.map((particle) => (
                        <div
                            key={particle.id}
                            className="hub-particle"
                            style={{
                                left: `${particle.x}%`,
                                top: `${particle.y}%`,
                                width: `${particle.size}px`,
                                height: `${particle.size}px`,
                                opacity: particle.opacity,
                                animationDuration: `${particle.duration}s`,
                                animationDelay: `${particle.delay}s`,
                            }}
                        />
                    ))}
                </div>
            </div>

            {/* Main Content */}
            <main className="hub-main">
                {/* Hub Logo Container */}
                <div className="hub-organism">
                    {/* Center Title */}
                    <div className="hub-title">
                        <span className="hub-title__brand">MODELERMAN</span>
                        <span className="hub-title__hub">HUB</span>
                    </div>
                    <svg
                        viewBox="-10 -10 120 120"
                        className="hub-organism__svg"
                        preserveAspectRatio="xMidYMid meet"
                    >
                        {/* Defs for gradients and filters */}
                        <defs>
                            <radialGradient id="centerGlow" cx="50%" cy="50%" r="50%">
                                <stop offset="0%" stopColor="#7c5cff" stopOpacity="0.6" />
                                <stop offset="100%" stopColor="#7c5cff" stopOpacity="0" />
                            </radialGradient>
                        </defs>

                        {/* Subtle Orbit Rings - no rotation animation */}
                        <g className="hub-orbits">
                            <circle
                                cx={centerX}
                                cy={centerY}
                                r="18"
                                fill="none"
                                stroke="rgba(124, 92, 255, 0.1)"
                                strokeWidth="0.3"
                                strokeDasharray="2 3"
                            />
                            <circle
                                cx={centerX}
                                cy={centerY}
                                r="25"
                                fill="none"
                                stroke="rgba(180, 155, 87, 0.08)"
                                strokeWidth="0.3"
                                strokeDasharray="1 4"
                            />
                        </g>

                        {/* Connection Lines */}
                        {hubNodes.map((node) => {
                            const nodeX = centerX + radius * Math.cos((node.angle * Math.PI) / 180);
                            const nodeY = centerY + radius * Math.sin((node.angle * Math.PI) / 180);
                            const isActive = activeNode === node.id;

                            return (
                                <g key={`line-${node.id}`}>
                                    {/* Glow line */}
                                    <line
                                        x1={centerX}
                                        y1={centerY}
                                        x2={nodeX}
                                        y2={nodeY}
                                        className={`hub-line hub-line--glow ${isActive ? "hub-line--active" : ""}`}
                                        style={{
                                            stroke: node.color,
                                            opacity: isActive ? 0.5 : 0.1
                                        }}
                                    />
                                    {/* Main line */}
                                    <line
                                        x1={centerX}
                                        y1={centerY}
                                        x2={nodeX}
                                        y2={nodeY}
                                        className={`hub-line ${isActive ? "hub-line--active" : ""}`}
                                        style={{
                                            stroke: isActive ? node.color : "rgba(255,255,255,0.15)",
                                            strokeWidth: isActive ? 0.8 : 0.3,
                                            opacity: isActive ? 0.8 : 0.3
                                        }}
                                    />
                                    {/* Pulse dot traveling on line */}
                                    <circle
                                        r={isActive ? 1 : 0.6}
                                        className="hub-pulse-dot"
                                        style={{
                                            fill: node.color,
                                            opacity: isActive ? 1 : 0.5
                                        }}
                                    >
                                        <animateMotion
                                            dur={`${3 + Math.random()}s`}
                                            repeatCount="indefinite"
                                            path={`M${centerX},${centerY} L${nodeX},${nodeY}`}
                                        />
                                    </circle>
                                </g>
                            );
                        })}

                        {/* Center Hub */}
                        <g className="hub-center">
                            {/* Outer pulsing ring */}
                            <circle
                                cx={centerX}
                                cy={centerY}
                                r="16"
                                fill="none"
                                stroke="rgba(124, 92, 255, 0.3)"
                                strokeWidth="0.5"
                                className="hub-center__pulse-ring"
                            >
                                <animate
                                    attributeName="r"
                                    values="14;20;14"
                                    dur="3s"
                                    repeatCount="indefinite"
                                />
                                <animate
                                    attributeName="opacity"
                                    values="0.5;0;0.5"
                                    dur="3s"
                                    repeatCount="indefinite"
                                />
                            </circle>

                            {/* Center glow */}
                            <circle
                                cx={centerX}
                                cy={centerY}
                                r="18"
                                fill="url(#centerGlow)"
                                className="hub-center__glow"
                            />

                            <circle
                                cx={centerX}
                                cy={centerY}
                                r="12"
                                className="hub-center__outer"
                            />
                            <circle
                                cx={centerX}
                                cy={centerY}
                                r="8"
                                className="hub-center__inner"
                            />
                            <circle
                                cx={centerX}
                                cy={centerY}
                                r="4"
                                className="hub-center__core"
                            />
                        </g>

                        {/* Nodes */}
                        {hubNodes.map((node) => {
                            const nodeX = centerX + radius * Math.cos((node.angle * Math.PI) / 180);
                            const nodeY = centerY + radius * Math.sin((node.angle * Math.PI) / 180);
                            const isActive = activeNode === node.id;

                            return (
                                <g
                                    key={node.id}
                                    className={`hub-node ${isActive ? "hub-node--active" : ""}`}
                                    onMouseEnter={() => handleNodeHover(node.id)}
                                    onMouseLeave={handleNodeLeave}
                                >
                                    {/* Node glow */}
                                    <circle
                                        cx={nodeX}
                                        cy={nodeY}
                                        r={isActive ? 10 : 8}
                                        className="hub-node__glow"
                                        style={{
                                            fill: node.color,
                                            opacity: isActive ? 0.5 : 0.2
                                        }}
                                    />
                                    {/* Node circle */}
                                    <circle
                                        cx={nodeX}
                                        cy={nodeY}
                                        r={isActive ? 5 : 4}
                                        className="hub-node__circle"
                                        style={{
                                            fill: isActive ? node.color : "rgba(255,255,255,0.2)",
                                            stroke: node.color,
                                        }}
                                    />
                                    {/* Small orbit */}
                                    <circle
                                        cx={nodeX}
                                        cy={nodeY}
                                        r={isActive ? 2.5 : 2}
                                        className="hub-node__core"
                                        style={{
                                            fill: node.color,
                                        }}
                                    />
                                </g>
                            );
                        })}
                    </svg>

                    {/* Node Labels (positioned absolutely) */}
                    {hubNodes.map((node, index) => {
                        const isActive = activeNode === node.id;

                        // Position labels outside the nodes
                        const labelOffset = 18;
                        const labelX = centerX + (radius + labelOffset) * Math.cos((node.angle * Math.PI) / 180);
                        const labelY = centerY + (radius + labelOffset) * Math.sin((node.angle * Math.PI) / 180);

                        const NodeWrapper = node.external ? "a" : Link;
                        const nodeProps = node.external
                            ? { href: node.url, target: "_blank", rel: "noopener noreferrer" }
                            : { to: node.url };

                        return (
                            <NodeWrapper
                                key={`label-${node.id}`}
                                {...nodeProps}
                                className={`hub-label ${isActive ? "hub-label--active" : ""}`}
                                style={{
                                    "--label-x": `${labelX}%`,
                                    "--label-y": `${labelY}%`,
                                    "--node-color": node.color,
                                    animationDelay: `${index * 0.5}s`,
                                    animationDuration: `${3 + index * 0.3}s`,
                                }}
                                onMouseEnter={() => handleNodeHover(node.id)}
                                onMouseLeave={handleNodeLeave}
                            >
                                <span className="hub-label__icon">{node.icon}</span>
                                <span className="hub-label__text">
                                    <span className="hub-label__title">{node.label}</span>
                                    <span className="hub-label__sub">{node.sublabel}</span>
                                </span>
                            </NodeWrapper>
                        );
                    })}
                </div>
            </main>
            <div className="hub-footer">
                <p className="hub-footer__tagline">
                    Tüm dijital hizmetleriniz tek çatı altında
                </p>

                {/* Action Buttons */}
                <div className="hub-footer__actions">
                    <a href="mailto:studio@modelerman.com" className="hub-footer__link">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                            <polyline points="22,6 12,13 2,6" />
                        </svg>
                        İletişim
                    </a>
                    <Link to="/client/login" className="hub-footer__link">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
                            <circle cx="12" cy="7" r="4" />
                        </svg>
                        Müşteri Girişi
                    </Link>
                    <button
                        className="hub-footer__link hub-footer__qr-btn"
                        onClick={() => setShowQR(true)}
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="3" y="3" width="7" height="7" />
                            <rect x="14" y="3" width="7" height="7" />
                            <rect x="3" y="14" width="7" height="7" />
                            <rect x="14" y="14" width="3" height="3" />
                            <rect x="18" y="14" width="3" height="3" />
                            <rect x="14" y="18" width="3" height="3" />
                            <rect x="18" y="18" width="3" height="3" />
                        </svg>
                        QR Kod
                    </button>
                </div>

                {/* Social Links */}
                <div className="hub-social">
                    <a href="https://instagram.com/modeler.man" target="_blank" rel="noopener noreferrer" className="hub-social__link" title="Instagram">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <rect x="2" y="2" width="20" height="20" rx="5" />
                            <circle cx="12" cy="12" r="4" />
                            <circle cx="18" cy="6" r="1" fill="currentColor" />
                        </svg>
                    </a>
                    <a href="https://modelerman.com/coming-soon" target="_blank" rel="noopener noreferrer" className="hub-social__link" title="YouTube">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <path d="M22.54 6.42a2.78 2.78 0 00-1.94-2C18.88 4 12 4 12 4s-6.88 0-8.6.46a2.78 2.78 0 00-1.94 2A29 29 0 001 11.75a29 29 0 00.46 5.33A2.78 2.78 0 003.4 19c1.72.46 8.6.46 8.6.46s6.88 0 8.6-.46a2.78 2.78 0 001.94-2 29 29 0 00.46-5.25 29 29 0 00-.46-5.33z" />
                            <polygon points="9.75 15.02 15.5 11.75 9.75 8.48 9.75 15.02" fill="currentColor" />
                        </svg>
                    </a>
                    <a href="https://modelerman.com/coming-soon" target="_blank" rel="noopener noreferrer" className="hub-social__link" title="LinkedIn">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <path d="M16 8a6 6 0 016 6v7h-4v-7a2 2 0 00-2-2 2 2 0 00-2 2v7h-4v-7a6 6 0 016-6z" />
                            <rect x="2" y="9" width="4" height="12" />
                            <circle cx="4" cy="4" r="2" />
                        </svg>
                    </a>
                    <a href="https://modelerman.com/coming-soon" target="_blank" rel="noopener noreferrer" className="hub-social__link" title="X (Twitter)">
                        <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                        </svg>
                    </a>
                    <a href="https://modelerman.com/coming-soon" target="_blank" rel="noopener noreferrer" className="hub-social__link" title="Behance">
                        <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M22 7h-7v-2h7v2zm1.726 10c-.442 1.297-2.029 3-5.101 3-3.074 0-5.564-1.729-5.564-5.675 0-3.91 2.325-5.92 5.466-5.92 3.082 0 4.964 1.782 5.375 4.426.078.506.109 1.188.095 2.14h-8.027c.13 3.211 3.483 3.312 4.588 2.029h3.168zm-7.686-4h4.965c-.105-1.547-1.136-2.219-2.477-2.219-1.466 0-2.277.768-2.488 2.219zm-9.574 6.988h-6.466v-14.967h6.953c5.476.081 5.58 5.444 2.72 6.906 3.461 1.26 3.577 8.061-3.207 8.061zm-3.466-8.988h3.584c2.508 0 2.906-3-.312-3h-3.272v3zm3.391 3h-3.391v3.016h3.341c3.055 0 2.868-3.016.05-3.016z" />
                        </svg>
                    </a>
                </div>
            </div>

            {/* QR Code Modal */}
            {showQR && (
                <div className="hub-modal" onClick={() => setShowQR(false)}>
                    <div className="hub-modal__content" onClick={(e) => e.stopPropagation()}>
                        <button className="hub-modal__close" onClick={() => setShowQR(false)}>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="18" y1="6" x2="6" y2="18" />
                                <line x1="6" y1="6" x2="18" y2="18" />
                            </svg>
                        </button>
                        <h3 className="hub-modal__title">Modelerman Hub</h3>
                        <p className="hub-modal__subtitle">QR kodu tarayarak paylaş</p>
                        <div className="hub-modal__qr">
                            {/* QR Code SVG - modelerman.com */}
                            <svg viewBox="0 0 200 200" className="hub-qr-code">
                                <rect width="200" height="200" fill="white" rx="16" />
                                <g fill="#0b0d14">
                                    {/* Simplified QR pattern */}
                                    <rect x="20" y="20" width="50" height="50" />
                                    <rect x="130" y="20" width="50" height="50" />
                                    <rect x="20" y="130" width="50" height="50" />
                                    <rect x="30" y="30" width="30" height="30" fill="white" />
                                    <rect x="140" y="30" width="30" height="30" fill="white" />
                                    <rect x="30" y="140" width="30" height="30" fill="white" />
                                    <rect x="40" y="40" width="10" height="10" />
                                    <rect x="150" y="40" width="10" height="10" />
                                    <rect x="40" y="150" width="10" height="10" />
                                    {/* Center logo area */}
                                    <rect x="80" y="80" width="40" height="40" rx="8" fill="#7c5cff" />
                                    <text x="100" y="105" textAnchor="middle" fill="white" fontSize="12" fontWeight="bold">M</text>
                                    {/* Random QR dots */}
                                    <rect x="80" y="20" width="10" height="10" />
                                    <rect x="100" y="20" width="10" height="10" />
                                    <rect x="80" y="40" width="10" height="10" />
                                    <rect x="110" y="40" width="10" height="10" />
                                    <rect x="20" y="80" width="10" height="10" />
                                    <rect x="40" y="90" width="10" height="10" />
                                    <rect x="20" y="100" width="10" height="10" />
                                    <rect x="50" y="110" width="10" height="10" />
                                    <rect x="140" y="80" width="10" height="10" />
                                    <rect x="160" y="90" width="10" height="10" />
                                    <rect x="170" y="100" width="10" height="10" />
                                    <rect x="130" y="110" width="10" height="10" />
                                    <rect x="80" y="130" width="10" height="10" />
                                    <rect x="100" y="140" width="10" height="10" />
                                    <rect x="110" y="160" width="10" height="10" />
                                    <rect x="130" y="130" width="10" height="10" />
                                    <rect x="150" y="140" width="10" height="10" />
                                    <rect x="170" y="150" width="10" height="10" />
                                    <rect x="140" y="170" width="10" height="10" />
                                    <rect x="160" y="170" width="10" height="10" />
                                </g>
                            </svg>
                        </div>
                        <p className="hub-modal__url">modelerman.com</p>
                    </div>
                </div>
            )}
        </div>
    );
}
